/**
 * ExportButton - Trigger PDF export with dropdown menu
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../../../../ui/dropdown-menu';
import { FileDown, Loader2 } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import type { ExportButtonProps } from '../../types';
import { useCauseReportExport } from '../../hooks';
import { ExportProgressModal } from './ExportProgressModal';
import { DEFAULT_EXPORT_SECTIONS } from '../../constants';

export function ExportButton({
  datasetId,
  filters,
  disabled,
  className,
}: ExportButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    exportReport,
    status,
    progress,
    downloadUrl,
    error,
    reset,
    isExporting,
  } = useCauseReportExport();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const handleExport = () => {
    setShowModal(true);
    exportReport({
      datasetId,
      filters,
      format: 'pdf',
      includeSections: DEFAULT_EXPORT_SECTIONS,
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Reset after a short delay to allow animation
    resetTimeoutRef.current = setTimeout(reset, 300);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled || isExporting}
            className={cn('rounded-full border-border/70 bg-background/90 shadow-sm', className)}
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="mr-2 h-4 w-4" />
            )}
            Export
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Export Report</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleExport}>
            <FileDown className="mr-2 h-4 w-4" />
            Export as PDF
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ExportProgressModal
        isOpen={showModal}
        onClose={handleCloseModal}
        status={status}
        progress={progress}
        downloadUrl={downloadUrl}
        error={error}
      />
    </>
  );
}
